import { onStep, getRibbonData, getMinimalizedVariant, getShowReason } from "./utils";
import Ribbon from "./ribbon";
import RibbonCont from "./cont";
import { store } from "~/state/store";
import { watch, watchDetail, watchRibbonContent, watchMapOverlays, watchRibbonEnabled } from "~/state/util";
import mapControls from "~/scene/control/mapcontrols";
import { setRibbonEnabled, setRibbonInitData, setRibbonMinimalized, setRibbonHidden } from "~/state/ribbon/state";
import { addListener } from "~/lib/signals";
import { ILoadRibbonData } from "./interfaces";
import { getDetailData } from "~/util/providers/ribbon";
import { RIBBON_TRANS } from "./trans";
import { ANIM_DURATION, ENABLE_RIBBON_ROBOTS, STEPS } from "./const";
import RibbonUp from "./up";
import { ribbonAnalyticsScroll, ribbonAnalyticsLoad, ribbonAnalyticsClick, ribbonAnalyticsShowHide, ribbonAnalyticsItemShow, TLoadContentReason } from "./analytics";
import { createHideTooltip, createMinimalizeTooltip } from "./tooltips";
import TooltipClose from "~/ui/tooltip-close";
import { IPoi } from "~/interfaces";
import { ribbonLog } from "./log";
import { IRibbonState } from "~/state/ribbon/interfaces";
import { CONFIG } from "~/config";
import { prepareSetting, setUserSettings } from "~/util/providers/mapybox";
import { ESETTINGS_KEYS } from "~/state/user/interfaces";
import { setCoordinates } from "~/state/map/state";

type TVisibility = "hidden" | "visible" | "minimalized";

class RibbonHandler {
	_ribbon: Ribbon;

	_ribbonUp: RibbonUp;

	_allControls: HTMLDivElement;

	_ribbonControl: HTMLDivElement;

	_isDetailOpen: boolean;

	_tooltips: {
		minimalized: TooltipClose;
		minimalizedState: boolean;
		hided: TooltipClose;
		hidedState: boolean,
	};

	_animCallback: () => void;

	_lastLoadedDetail: {
		source: string;
		id: number;
	};

	_firstLoad: boolean;

	_phantomLoad: boolean;

	_hideMinimalizedRibbonOnLoading: boolean;

	constructor() {
		this._ribbon = null;
		this._allControls = document.getElementById("all-controls") as HTMLDivElement;
		this._ribbonControl = document.getElementById("ribbon-control") as HTMLDivElement;
		this._isDetailOpen = false;
		this._tooltips = {
			minimalized: null,
			minimalizedState: false,
			hided: null,
			hidedState: false,
		};
		this._animCallback = null;
		this._lastLoadedDetail = {
			source: "",
			id: 0,
		};
		this._firstLoad = true;
		this._phantomLoad = false;
		this._hideMinimalizedRibbonOnLoading = false;

		this._build();
		// init stav
		store.dispatch(setRibbonHidden(true));
		store.dispatch(setRibbonMinimalized(false));
		this._setUiVisibility("hidden");

		const wtRibbonContent = watch(store.getState, "ribbon", watchRibbonContent);
		const wtRibbonEnabled = watch(store.getState, "ribbon", watchRibbonEnabled);
		const wtDetail = watch(store.getState, "detail", watchDetail);
		const wtMapOverlays = watch(store.getState, "map", watchMapOverlays);

		// zmena detailu
		store.subscribe(wtDetail((newDetail, oldDetail) => {
			if (newDetail.data && !oldDetail.data) {
				const curData = getRibbonData();
				let sendLoadContent = true;
				let reason: TLoadContentReason = "none";

				if (curData.canBeShown) {
					// povoleny - nacteme data
					if (curData.ribbonState.enabled) {
						sendLoadContent = false;
						ribbonLog("load detail - succcess");
					} else if (curData.ribbonState.userVariant === "A") {
						// skryjeme, ale nacteme data
						//store.dispatch(setRibbonHidden(false));
						//store.dispatch(setRibbonMinimalized(true));
						reason = "ribbon_hidden";
						//this._minimalize("minimalized");
					}

					this._loadRibbonData({
						detailState: curData.detailState,
						ribbonState: curData.ribbonState,
					});
				}

				if (sendLoadContent) {
					const showReason = reason === "none"
						? getShowReason()
						: reason;

					ribbonAnalyticsLoad({
						action: "load_content",
						section: "ribbon",
						module: "content",
						success: "unsuccessful",
						pageType: "poi",
						reason: showReason,
						source: newDetail.data.source,
						id: newDetail.data.id as number,
						typeId: newDetail.data.typeId,
					});
				}
			}
		}));
		// nastaveni viditelnosti pri zmene contentu
		store.subscribe(wtRibbonContent(ribbonState => {
			if (!ribbonState.activeContents.length) {
				return;
			}

			const curData = getRibbonData();

			if (curData.ribbonState.enabled) {
				if (curData.canBeShown) {
					this._showRibbon(curData.ribbonState);
				} else {
					this._hideRibbon();
				}
			} else if (curData.ribbonState.userVariant === "A" && curData.canBeShown) {
				this._showRibbon(curData.ribbonState);
			} else {
				this._hideRibbon();
			}
		}));
		// skryti/zobrazeni ribbonu
		store.subscribe(wtRibbonEnabled(ribbonState => {
			this._ribbon.setShowButton(!ribbonState.enabled, ribbonState.enabled ? RIBBON_TRANS.DO_NOT_SHOW : RIBBON_TRANS.SHOW_AUTOMATICALLY);

			if (ribbonState.enabled) {
				if (ribbonState.enabledLocation === "ribbon") {
					this._showRibbon(ribbonState);
				} else {
					// mohlo byt skryte pres uzivatele A
					store.dispatch(setRibbonMinimalized(false));
					this._hideRibbon();
				}
			} else {
				const variant = getMinimalizedVariant() || ribbonState.enabledLocation === "settings";

				if (variant) {
					store.dispatch(setRibbonHidden(true));
				} else {
					store.dispatch(setRibbonMinimalized(true));
				}

				this._minimalize(variant ? "hidden" : "minimalized").then(() => {
					if (variant && ribbonState.enabledLocation === "ribbon" && !this._tooltips.hidedState) {
						this._tooltips.hided = createHideTooltip(() => {
							this._tooltips.hided = null;
						});
						this._tooltips.hidedState = true;
					} else if (!variant && !this._tooltips.minimalizedState) {
						this._tooltips.minimalized = createMinimalizeTooltip(() => {
							this._tooltips.minimalized = null;
						});
						this._tooltips.minimalizedState = true;
					}
				});
			}
		}));
		// zmena overlay vrstev
		store.subscribe(wtMapOverlays(() => {
			const curData = getRibbonData();

			if (curData.ribbonState.enabled) {
				if (curData.canBeShown) {
					if (this._ribbon.content) {
						this._showRibbon(curData.ribbonState);
					} else {
						// nacteme
						this._loadRibbonData({
							detailState: curData.detailState,
							ribbonState: curData.ribbonState,
						});
					}
				} else if (!curData.ribbonState.hidden) {
					this._hideRibbon();
				}
			} else if (curData.ribbonState.userVariant === "A") {
				if (curData.isPanoActive) {
					this._hideRibbon();
				} else {
					this._minimalize("minimalized");
				}
			}
		}));
	}

	setPhantomLoad() {
		// prvotni nacteni
		const curData = getRibbonData();
		const phantomLoad = curData.canBeShown && ENABLE_RIBBON_ROBOTS;

		this._phantomLoad = phantomLoad;

		return this._phantomLoad;
	}

	_clearTooltips() {
		this._tooltips.minimalized && this._tooltips.minimalized.close();
		this._tooltips.hided && this._tooltips.hided.close();
	}

	_clearAnim() {
		if (this._animCallback) {
			this._animCallback();
			this._animCallback = null;
		}
	}

	_minimalize(visibility: TVisibility) {
		return new Promise(resolve => {
			this._clearAnim();
			this._clearTooltips();
			this._animCallback = onStep(ANIM_DURATION, STEPS, name => {
				switch (name) {
					case "start":
						this._allControls.classList.add("anim-hide");
						this._ribbon?.syncPort();
						break;

					case "step":
						this._ribbon?.syncPort();
						mapControls.syncPortPointers();
						break;

					case "end": {
						this._allControls.classList.remove("anim-hide");
						this._setUiVisibility(visibility);
						mapControls.syncPortPointers();
						resolve({});
						break;
					}

					default:
				}
			});
		});
	}

	_unMinimalize() {
		return new Promise(resolve => {
			this._clearAnim();
			this._clearTooltips();
			// init
			this._setUiVisibility("minimalized");
			this._animCallback = onStep(ANIM_DURATION, STEPS, name => {
				switch (name) {
					case "start":
						this._allControls.classList.add("anim-show");
						this._ribbon?.syncPort();
						break;

					case "step":
						this._ribbon?.syncPort();
						mapControls.syncPortPointers();
						break;

					case "end": {
						this._allControls.classList.remove("anim-hide");
						this._setUiVisibility("visible");
						this._handleVisibleItems(0, store.getState().detail.data);
						mapControls.syncPortPointers();
						resolve({});
						break;
					}

					default:
				}
			});
		});
	}

	_build() {
		const initState = store.getState().ribbon;
		const ribbon = new Ribbon({
			debug: initState.debug,
			onClick: clickData => {
				switch (clickData.name) {
					case "down":
						ribbonAnalyticsShowHide({
							action: "down",
						});
						store.dispatch(setRibbonMinimalized(true));
						this._minimalize("minimalized");
						break;

					case "left": {
						const curState = store.getState();

						ribbonAnalyticsScroll(false);
						this._handleVisibleItems(clickData.step, curState.detail.data);
						break;
					}

					case "right": {
						const curState = store.getState();

						ribbonAnalyticsScroll(true);
						this._handleVisibleItems(clickData.step, curState.detail.data);
						break;
					}

					case "visibility": {
						// switch
						const curState = store.getState().ribbon;
						const enabled = !curState.enabled;
						const setting = prepareSetting(ESETTINGS_KEYS.RIBBON_ENABLED, enabled);

						setUserSettings(setting);
						store.dispatch(setRibbonEnabled({
							enabled,
							enabledLocation: "ribbon",
						}));
						ribbonAnalyticsShowHide({
							action: enabled ? "restore" : "dont_show",
						});
						break;
					}

					case "news":
						ribbonAnalyticsClick("article", {
							url: clickData.data.linkUrl,
							name: clickData.data.desc,
						});
						window.open(clickData.data.linkUrl);
						break;

					case "photos":
						ribbonAnalyticsClick("photo");
						Mapy.getComponent("photoDetail").setData({
							source: clickData.data.source,
							id: clickData.data.id,
							onClose: (type, data) => {
								if (type === "close-map-click" && data && data.mark) {
									store.dispatch(setCoordinates({
										longitude: data.mark.lon,
										latitude: data.mark.lat,
										zoom: 19,
										animate: true,
									}));
								}
							},
						}).activate();
						break;

					case "reviews":
						ribbonAnalyticsClick("review", {
							id: clickData.data.id as number,
							source: clickData.data.source,
						});
						Mapy.getComponent("detail").setState({
							source: clickData.data.source,
							id: clickData.data.id,
							//zoomToReviews: true,
							recenze: "1",
						}).then(() => {
							Mapy.getComponent("history").save();
						});
						break;

					case "pois":
						ribbonAnalyticsClick("poi", {
							source: clickData.data.poiSource,
							id: clickData.data.poiId,
							name: clickData.data.poiTitle,
							typeId: clickData.data.poiTypeId,
						});
						Mapy.getComponent("detail").setState({
							source: clickData.data.poiSource,
							id: clickData.data.poiId,
						}).then(() => {
							Mapy.getComponent("history").save();
						});
						break;

					default:
				}
			},
		});

		this._ribbonControl.append(ribbon.container);
		ribbon.setShowButton(!initState.enabled, initState.enabled ? RIBBON_TRANS.DO_NOT_SHOW : RIBBON_TRANS.SHOW_AUTOMATICALLY);

		addListener(this, "width-change-start", (eventData: { data: { newWidth: number; }}) => {
			if (eventData.data.newWidth === 0) {
				this._ribbon.contentParking();
				this._hideRibbon();
			}
		});
		addListener(this, "width-change", (eventData: { data: { newWidth: number; }}) => {
			if (eventData.data.newWidth === 1) {
				const curData = getRibbonData();

				if (curData.canBeShown) {
					this._showRibbon(curData.ribbonState);
				}
			}
		});
		addListener(this, "resize", () => {
			this._syncRibbon();
		});
		addListener(this, "print-page", () => {
			// zacatek tisku
			this._hideRibbon();
			this._ribbon.container.remove();
		});
		addListener(this, "print-done", () => {
			this._ribbonControl.append(ribbon.container);
			const curData = getRibbonData();

			if (curData.canBeShown) {
				this._showRibbon(curData.ribbonState);
			}
		});

		this._ribbon = ribbon;
		// ousko
		this._ribbonUp = mapControls.addRibbonUp(initState.debug);
		this._ribbonUp.onClick(() => {
			store.dispatch(setRibbonMinimalized(false));
			this._unMinimalize().then(() => {
				const curState = store.getState();

				if (!curState.ribbon.enabled && curState.detail.data) {
					this._loadRibbonData({
						detailState: curState.detail,
						ribbonState: curState.ribbon,
					});
				}
			});
			ribbonAnalyticsShowHide({
				action: "up",
			});
		});
		this._ribbonUp.hide();
		// uprava resizeru
		mapControls.moveResizerToMapControls();
		mapControls.syncPortPointers();
	}

	_syncRibbon() {
		this._ribbon.contentParking();
		this._ribbon.syncPort();
	}

	async _loadRibbonData({
		detailState,
		ribbonState,
	}: ILoadRibbonData) {
		if (!detailState.data) {
			return;
		}

		if (this._ribbon.content) {
			// stejny kontent jako predtim
			if (detailState.data.source === this._lastLoadedDetail.source && detailState.data.id === this._lastLoadedDetail.id) {
				return;
			}
		}

		this._ribbon.debugTitle = "Načítám...";
		this._ribbonUp.debugTitle = "Načítám...";

		this._ribbon.loader = true;
		this._hideMinimalizedRibbonOnLoading && this._allControls.classList.add("loading");

		const czData = detailState.centerZoom;
		const data = await getDetailData({
			poi: detailState.data,
			center: [czData.lon, czData.lat],
			zoom: czData.zoom,
			bbox: czData.bbox,
			geomBbox: czData.geomBbox,
		});

		this._clearLastLoadedDetail();
		this._ribbon.loader = false;
		this._hideMinimalizedRibbonOnLoading && this._allControls.classList.remove("loading");

		let sendLoadContent = false;

		if (data) {
			// ulozime data
			this._lastLoadedDetail.source = detailState.data.source;
			this._lastLoadedDetail.id = detailState.data.id as number;

			this._ribbon.debugColor();

			if (data.canBeShown) {
				const newContent = new RibbonCont({
					data,
				});

				this._ribbon.setContent(newContent).then(() => {
					if (ribbonState.enabled) {
						ribbonAnalyticsLoad({
							action: "load_content",
							section: "ribbon",
							module: "content",
							success: "successful",
							contentFound: {
								articles: data.news.length,
								photos: data.photos.length,
								reviews: data.reviews.length,
								poi: data.pois.length,
							},
							contentShown: newContent.loadVisibleItems,
							pageType: "poi",
							source: detailState.data.source,
							id: detailState.data.id as number,
							typeId: detailState.data.typeId,
						});

						if (!ribbonState.minimalized) {
							this._handleVisibleItems(0, detailState.data);
						}

						// zkusime, jestli jdeme furt videt
						if (getRibbonData().canBeShown) {
							this._showRibbonAfterData(ribbonState);
						} else {
							this._hideRibbon();
						}
					} else if (ribbonState.userVariant === "A" && getRibbonData().canBeShown) {
						// zobrazime min. verzi
						store.dispatch(setRibbonHidden(false));
						store.dispatch(setRibbonMinimalized(true));
						this._minimalize("minimalized");
					}
				});
				this._ribbon.debugColor();

				const msg = `Mám data, vše cajk, načtení za ${(data.duration.total / 1000).toFixed(2)}s`;

				ribbonLog(msg);
				this._ribbon.debugTitle = msg;
				this._ribbonUp.debugTitle = msg;
			} else {
				this._hideRibbon();
				this._ribbon.setContent(null);
				this._ribbon.debugColor("red");

				const msg = `Přišla data, ale min content selhal`;

				ribbonLog(msg);
				this._ribbon.debugTitle = msg;
				this._ribbonUp.debugTitle = msg;
				sendLoadContent = true;
			}
		} else if (!this._ribbon.content) {
			this._ribbon.debugColor("red");

			const msg = `Nepřišla data z BE`;

			ribbonLog(msg);
			this._ribbon.debugTitle = msg;
			this._ribbonUp.debugTitle = msg;
			sendLoadContent = true;
		}

		if (sendLoadContent) {
			ribbonAnalyticsLoad({
				action: "load_content",
				section: "ribbon",
				module: "content",
				success: "unsuccessful",
				pageType: "poi",
				reason: "insufficient_content",
				source: detailState.data.source,
				id: detailState.data.id as number,
				typeId: detailState.data.typeId,
			});
		}

		if (this._phantomLoad) {
			Mapy.setPhantomMeta(200);
			this._phantomLoad = false;
		}
	}

	_handleVisibleItems(step: number, detailData: IPoi) {
		if (!this._ribbon.content) {
			return;
		}

		const visibleItems = this._ribbon.content.getVisibleItems(step, "poi", detailData);

		if (visibleItems.length) {
			visibleItems.forEach(item => ribbonAnalyticsItemShow(item));
		}
	}

	_setUiVisibility(visibility: TVisibility) {
		this._allControls.classList.remove("anim-show");
		this._allControls.classList.remove("anim-hide");
		this._allControls.classList.remove("ribbon-up-visible");
		this._allControls.classList.remove("ribbon-disabled");
		this._ribbonUp.hide();

		// skryte
		if (visibility === "hidden") {
			this._allControls.classList.add("ribbon-disabled");
		} else if (visibility === "minimalized") {
			this._allControls.classList.add("ribbon-up-visible");
			this._ribbonUp.show();
		}
	}

	_showRibbon(ribbonState: IRibbonState) {
		this._clearAnim();

		if (ribbonState.minimalized) {
			if (this._ribbon.content) {
				store.dispatch(setRibbonHidden(false));
				this._setUiVisibility("minimalized");
			}
		} else if (this._ribbon.content) {
			store.dispatch(setRibbonHidden(false));
			this._unMinimalize();
		}
	}

	_showRibbonAfterData(ribbonState: IRibbonState) {
		if (ribbonState.hidden) {
			store.dispatch(setRibbonHidden(false));

			if (ribbonState.minimalized) {
				this._setUiVisibility("minimalized");
			} else {
				this._unMinimalize();
			}
		}
	}

	_hideRibbon() {
		store.dispatch(setRibbonHidden(true));
		this._minimalize("hidden");
	}

	_clearLastLoadedDetail() {
		this._lastLoadedDetail.source = "";
		this._lastLoadedDetail.id = 0;
	}
}

let ribbonHandler: RibbonHandler | null = null;

export function initRibbon() {
	const cusEnabled = Mapy.CUS.ribbon?.enabled || Mapy.CUS.userweb?.ribbon;

	// klikatko
	if (cusEnabled) {
		const userVariant = Mapy.CUS.ribbon?.user ? "B" : "A";
		const enabled = CONFIG.ribbonEnabled === undefined || CONFIG.ribbonEnabled;

		// nastavime store
		store.dispatch(setRibbonInitData({
			debug: Mapy.CUS.ribbon?.debug || false,
			userVariant,
			enabled,
		}));

		ribbonHandler = new RibbonHandler();
	}
}

export function getRibbon() {
	return ribbonHandler;
}
